/**
 * A weed-strip sprayer: drives down every row and wets only a strip, such as
 * 50 cm either side of the driven row (1.0 m per pass).
 *
 * Its calibrated L/ha is per SPRAYED hectare, which is how herbicide labels
 * state a band application. So the ground a spray treats, and every
 * per-hectare product total, is the block's area times
 *
 *     strip width / row spacing
 *
 * (1.0 m on 4 m rows is 25%). The spray stores one coverage % across all its
 * targets, so blocks with different row spacings are weighted by area.
 */

export interface StripTarget {
  name: string;
  areaHa: number;
  /** The planting's row spacing; for a block or farm, the area-weighted mean. */
  rowSpacingM: number | null;
}

export interface StripCoverage {
  stripWidthM: number;
  /** Null when any target has no row spacing: a part-guess is no answer. */
  coveragePct: number | null;
  sprayedHa: number | null;
  totalHa: number;
  /** Targets with no row spacing recorded, by name. */
  missingRowSpacing: string[];
}

/**
 * The share of the spray's ground a strip sprayer wets.
 *
 * A strip wider than the row (a 1.2 m strip on 1 m nursery rows) is capped at
 * the whole row: it cannot wet more than the block.
 */
export function stripCoverage(stripWidthM: number, targets: StripTarget[]): StripCoverage {
  const totalHa = targets.reduce((s, t) => s + (t.areaHa > 0 ? t.areaHa : 0), 0);
  const missingRowSpacing = targets
    .filter(t => !(t.rowSpacingM != null && t.rowSpacingM > 0))
    .map(t => t.name);

  if (!(stripWidthM > 0) || !(totalHa > 0) || missingRowSpacing.length) {
    return { stripWidthM, coveragePct: null, sprayedHa: null, totalHa, missingRowSpacing };
  }

  const sprayedHa = targets.reduce((s, t) => {
    if (!(t.areaHa > 0)) return s;
    return s + t.areaHa * Math.min(1, stripWidthM / (t.rowSpacingM as number));
  }, 0);

  return {
    stripWidthM,
    // Two decimals: the server keeps the percentage as typed, and the
    // effective area is worked out from it.
    coveragePct: Math.round((sprayedHa / totalHa) * 10000) / 100,
    sprayedHa: Math.round(sprayedHa * 10000) / 10000,
    totalHa,
    missingRowSpacing,
  };
}

/**
 * Area-weighted mean row spacing of the plantings inside a block or farm, as
 * the server works it out (`rowSpacingForTarget`): a block mostly on 4.5 m rows
 * with one 3 m corner resolves close to 4.5, not to the midpoint.
 */
export function weightedRowSpacing(
  plantings: Array<{ area_hectares?: unknown; row_spacing_meters?: unknown }>,
): number | null {
  let weighted = 0;
  let area = 0;
  for (const p of plantings) {
    const ha = Number(p.area_hectares);
    const rs = Number(p.row_spacing_meters);
    if (!(ha > 0) || !(rs > 0)) continue;
    weighted += ha * rs;
    area += ha;
  }
  return area > 0 ? weighted / area : null;
}
